/**
 * Press Releases Compact Block Async Loader
 * Handles asynchronous loading of compact press release blocks via AJAX
 */
export function initPressReleasesCompactAsync() {
    const containers = document.querySelectorAll('[data-press-releases-compact-async]');
    if (!containers.length) return;

    containers.forEach(container => {
        loadCompactPressReleases(container);
    });
}

/**
 * Load compact press releases for a specific container
 * @param {HTMLElement} container - The container element
 */
async function loadCompactPressReleases(container) {
    const loadingState = container.querySelector('[data-loading-state]');
    const contentContainer = container.querySelector('[data-content-container]');
    const errorState = container.querySelector('[data-error-state]');

    // Get configuration from data attributes
    const limit = parseInt(container.dataset.limit || '3');
    const isRegulatory = container.dataset.isRegulatory === 'true';

    try {
        // Build API URL
        const apiUrl = new URL('/api/press-releases/load-more', window.location.origin);
        apiUrl.searchParams.append('offset', '0');
        apiUrl.searchParams.append('limit', limit.toString());
        apiUrl.searchParams.append('regulatory', isRegulatory.toString());
        
        // Add language from document
        const lang = document.documentElement.lang || 'en';
        apiUrl.searchParams.append('lang', lang);

        const response = await fetch(apiUrl.toString(), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load press releases');
        }

        const data = await response.json();

        // Hide loading state
        if (loadingState) {
            loadingState.style.display = 'none';
        }

        // Render items if available
        if (data.items && data.items.length > 0) {
            renderCompactItems(data.items, contentContainer);
            contentContainer.classList.remove('hidden');
        } else {
            // Show error state if no items
            if (errorState) {
                const errorMessage = errorState.querySelector('p');
                if (errorMessage) {
                    const dictionary = window.dictionary || { noPressReleasesAvailable: 'No press releases available at this time.' };
                    errorMessage.textContent = dictionary.noPressReleasesAvailable;
                }
                errorState.classList.remove('hidden');
            }
        }

    } catch (error) {
        console.error('Error loading compact press releases:', error);

        // Hide loading state
        if (loadingState) {
            loadingState.style.display = 'none';
        }

        // Show error state
        if (errorState) {
            errorState.classList.remove('hidden');
        }
    }
}

/**
 * Render compact press release items
 * @param {Array} items - Array of press release items
 * @param {HTMLElement} container - The container to render into
 */
function renderCompactItems(items, container) {
    const grid = document.createElement('div');
    grid.className = 'grid gap-46';
    
    if (items.length === 2) {
        grid.classList.add('lg:grid-cols-2');
    }
    else if(items.length > 2) {
        grid.classList.add('lg:grid-cols-3');
    }

    items.forEach((item, index) => {
        const isFirst = index === 0;
        const isRegulatory = item.isRegulatory || false;
        const publishDate = item.publishDate ? new Date(item.publishDate) : null;

        const article = document.createElement('a');
        article.href = `${window.settings.pressReleasesPageUrl}/${item.slug}`;
        article.className = 'flex flex-col group';

        // Header with date and tag
        const header = document.createElement('div');
        header.className = 'font-bold clamp-[text,13px,15px] leading-none uppercase border-b-1 border-black/12 clamp-[pb,22,24] clamp-[mb,20px,36px]';

        if (publishDate) {
            const time = document.createElement('time');
            time.className = 'mr-10';
            time.textContent = formatDate(publishDate);
            header.appendChild(time);
        }

        if (isRegulatory) {
            const dictionary = window.dictionary || { regulatory: 'Regulatory' };
            const tagSpan = document.createElement('span');
            tagSpan.className = 'uppercase text-black/70';
            tagSpan.innerHTML = '<span class="mr-10">&mdash;</span> ' + dictionary.regulatory;
            header.appendChild(tagSpan);
        }

        article.appendChild(header);

        // Content wrapper
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'flex-1 flex flex-col justify-between items-start';

        // Title
        const titleP = document.createElement('p');
        titleP.className = `clamp-[text,18px,20px] clamp-[mb,18px,36px] md:pr-30`;
        titleP.textContent = item.title || '';
        contentWrapper.appendChild(titleP);

        // Read more label (span, not anchor, since parent is already an anchor)
        const readMoreLabel = createReadMoreLabel();
        contentWrapper.appendChild(readMoreLabel);

        article.appendChild(contentWrapper);
        grid.appendChild(article);
    });

    container.appendChild(grid);
}

/**
 * Create "Read more" label element (span for use inside anchor)
 */
function createReadMoreLabel() {
    const dictionary = window.dictionary || { readMore: 'Read more' };
    const span = document.createElement('span');
    span.className = 'inline-flex u_small items-end gap-10 font-bold text-link group-hover:text-gold duration-300 transition uppercase clamp-[text,13px,15px]';

    const textNode = document.createTextNode(dictionary.readMore + ' ');
    span.appendChild(textNode);

    const iconSpan = document.createElement('span');
    iconSpan.className = 'text-link-icon';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'group-hover:translate-x-8 transition duration-300');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '36');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 36 24');
    svg.setAttribute('fill', 'none');

    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M5 12L31 12');
    path1.setAttribute('class', 'group-hover:!stroke-gold transition duration-300');
    path1.setAttribute('stroke', 'black');
    path1.setAttribute('stroke-width', '2');
    path1.setAttribute('stroke-linecap', 'round');
    path1.setAttribute('stroke-linejoin', 'round');

    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M24 5L31 12L24 19');
    path2.setAttribute('class', 'group-hover:!stroke-gold transition duration-300');
    path2.setAttribute('stroke', 'black');
    path2.setAttribute('stroke-width', '2');
    path2.setAttribute('stroke-linecap', 'round');
    path2.setAttribute('stroke-linejoin', 'round');

    svg.appendChild(path1);
    svg.appendChild(path2);
    iconSpan.appendChild(svg);
    span.appendChild(iconSpan);

    return span;
}

/**
 * Format date as "February 4, 2026"
 */
function formatDate(date) {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const locale = document.documentElement.lang || 'en';
    return date.toLocaleDateString(locale, options);
}
